import { DateTime } from 'luxon'
import { getTimeZoneAbbreviation } from './getTimeZoneAbbreviation'
import countriesAndTimezones from 'countries-and-timezones'

interface TimeZoneInfo {
  name: string // IANA name (e.g., "America/Los_Angeles")
  offset: string // Formatted offset (e.g., "-08:00")
  label: string // Formatted label (e.g., "(UTC-08:00) PST (Los Angeles)")
}

/**
 * Returns a list of all canonical IANA time zones.  They are the ones that
 * are not aliases of other time zones.
 */
const canonicalTimeZones = Object.values(
  countriesAndTimezones.getAllTimezones(),
)
  .filter((zone) => !zone.aliasOf)
  .map((zone) => zone.name)

export function listAllTimeZones(date = DateTime.now()): TimeZoneInfo[] {
  return canonicalTimeZones
    .map<TimeZoneInfo>((zoneName: string) => {
      const zoneDateTime = date.setZone(zoneName)

      const offsetFormatted = zoneDateTime
        .toFormat('ZZ')
        .replace(/^([+-])(\d{2})(\d{2})$/, '$1$2:$3') // Convert +0500 to +05:00 format

      const abbreviation = getTimeZoneAbbreviation(zoneName, zoneDateTime)
      const locationName = (zoneName.split('/').pop() ?? zoneName).replace(
        /_/g,
        ' ',
      )

      const label = `(UTC${offsetFormatted}) ${abbreviation} (${locationName})`

      return {
        name: zoneName,
        offset: offsetFormatted,
        label,
      }
    })
    .sort((a, b) => {
      // First compare by offset
      const offsetCompare = a.offset.localeCompare(b.offset)
      if (offsetCompare !== 0) return offsetCompare

      // If offsets are the same, compare by name
      return a.name.localeCompare(b.name)
    })
}
